/**
 * The action — every slip on the book, biggest market first.
 *
 * The board answers "what can I bet"; My bets answers "what am I holding".
 * Neither answers the question the table actually asks all week, which is
 * "who's on him, and for how much?" — and getting to it meant opening
 * thirteen markets one at a time. This is that list, ranked by the money.
 *
 * Everybody sees it, because everybody already receives it: the server
 * stopped scoping slips to their owner on the grounds that twelve friends
 * shouting numbers across a table are not protected by hiding them. Only
 * CONFIRMED money is ranked — a slip awaiting Venmo is an intention, and it
 * says so in its own muted line rather than inflating the totals.
 */

import { useState } from 'react'
import { marketExposure, type BookBet, type BookMarket } from '../engine/book'
import { formatAmerican, formatMoney, payoutCents } from '../engine/odds'
import type { Tournament } from '../engine/types'
import { playerName } from '../select'
import type { Book } from '../store'
import { Empty, SectionRule } from '../ui'

type View = 'money' | 'payout' | 'man' | 'new'

/** Every man with money out, biggest first — with his slips underneath. */
function ByMan({
  doc,
  bets,
  markets,
  me,
}: {
  doc: Tournament
  bets: BookBet[]
  markets: BookMarket[]
  me: string
}) {
  const live = bets.filter((b) => b.status !== 'void')
  const byMan = new Map<string, BookBet[]>()
  for (const b of live) byMan.set(b.player_id, [...(byMan.get(b.player_id) ?? []), b])
  const rows = [...byMan.entries()]
    .map(([id, slips]) => ({
      id,
      slips: [...slips].sort((a, b) => b.stake_cents - a.stake_cents),
      down: slips.reduce((n, b) => n + b.stake_cents, 0),
      owed: slips.filter((b) => b.status === 'pending').reduce((n, b) => n + b.stake_cents, 0),
      toWin: slips.reduce((n, b) => n + payoutCents(b.stake_cents, b.odds_at_placement), 0),
    }))
    .sort((a, b) => b.down - a.down)
  const nameOf = (id: string) => {
    const m = markets.find((x) => x.id === id)
    return m?.subject_id ? playerName(doc, m.subject_id) : (m?.description ?? 'a market')
  }
  return (
    <>
      <SectionRule>Who has the most out there</SectionRule>
      <ul className="divide-y divide-rule border-t border-rule">
        {rows.map((r) => (
          <li key={r.id} className="px-4 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className={`text-sm ${r.id === me ? 'bc-name' : ''}`}>
                {playerName(doc, r.id)}
              </span>
              <span className="bc-num text-base shrink-0">{formatMoney(r.down)}</span>
            </div>
            <div className="text-[12px] text-mute leading-snug">
              {r.slips.length} slip{r.slips.length === 1 ? '' : 's'} · stands to win{' '}
              {formatMoney(r.toWin)}
              {r.owed > 0 && (
                <span className="text-signal"> · {formatMoney(r.owed)} not in yet</span>
              )}
            </div>
            <ul className="mt-0.5">
              {r.slips.map((b, i) => (
                <li key={i} className="text-[12px] text-mute leading-snug">
                  {formatMoney(b.stake_cents)} on {nameOf(b.odds_id)} at{' '}
                  {formatAmerican(b.odds_at_placement)}
                  {b.status === 'pending' && ' · awaiting Venmo'}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </>
  )
}

/** The book as a feed: last slip struck at the top. */
function Newest({
  doc,
  bets,
  markets,
  me,
}: {
  doc: Tournament
  bets: BookBet[]
  markets: BookMarket[]
  me: string
}) {
  const slips = bets
    .filter((b) => b.status !== 'void')
    .slice()
    .reverse()
  const nameOf = (id: string) => {
    const m = markets.find((x) => x.id === id)
    return m?.subject_id ? playerName(doc, m.subject_id) : (m?.description ?? 'a market')
  }
  return (
    <>
      <SectionRule>Newest first</SectionRule>
      <ul className="divide-y divide-rule border-t border-rule">
        {slips.map((b, i) => (
          <li key={i} className="px-4 py-2 flex items-baseline justify-between gap-3">
            <span className="min-w-0">
              <span className={`text-sm ${b.player_id === me ? 'bc-name' : ''}`}>
                {playerName(doc, b.player_id)}
              </span>
              <span className="text-[12px] text-mute block leading-snug">
                {formatMoney(b.stake_cents)} on {nameOf(b.odds_id)} at{' '}
                {formatAmerican(b.odds_at_placement)}
                {b.status === 'pending' && ' · awaiting Venmo'}
              </span>
            </span>
            <span className="text-[12px] text-mute shrink-0 whitespace-nowrap">
              to win {formatMoney(payoutCents(b.stake_cents, b.odds_at_placement))}
            </span>
          </li>
        ))}
      </ul>
    </>
  )
}

export function Action({ doc, book, me }: { doc: Tournament; book: Book; me: string }) {
  const [open, setOpen] = useState<string | null>(null)
  /*
   * One list, four questions. The money is the default because it is the
   * one the table asks; "by man" answers the other one everybody asks
   * ("what has Thomas got out there?"), and the flat newest-first view is
   * the closest thing the book has to a feed.
   */
  const [view, setView] = useState<View>('money')

  const markets = book.odds as unknown as BookMarket[]
  const bets = book.bets as unknown as BookBet[]

  const live = bets.filter((b) => b.status === 'confirmed')
  const pending = bets.filter((b) => b.status === 'pending')

  /*
   * Ranked on every slip placed, confirmed or not — Scott's call, and the
   * right one for this screen: the table's question is "who is on him", and
   * a man who has called his bet across the table is on him whether or not
   * the Venmo has cleared. The EXPOSURE figures still count confirmed money
   * only; that is a different question with real money attached.
   */
  const rows = markets
    .map((m) => {
      const e = marketExposure(m, bets)
      const slips = bets
        .filter((b) => b.odds_id === m.id && b.status !== 'void')
        .sort((a, b) => b.stake_cents - a.stake_cents)
      const placed = slips.reduce((n, b) => n + b.stake_cents, 0)
      return { market: m, exposure: e, slips, placed }
    })
    .filter((r) => r.slips.length > 0)
    .sort((a, b) =>
      view === 'payout' ? b.exposure.liabilityCents - a.exposure.liabilityCents : b.placed - a.placed,
    )

  const staked = [...live, ...pending].reduce((n, b) => n + b.stake_cents, 0)
  const biggest = [...live, ...pending].sort((a, b) => b.stake_cents - a.stake_cents)[0]
  /* Who has the most money out there — the table's favourite argument. */
  const byBettor = new Map<string, number>()
  for (const b of [...live, ...pending])
    byBettor.set(b.player_id, (byBettor.get(b.player_id) ?? 0) + b.stake_cents)
  const heaviest = [...byBettor.entries()].sort((a, b) => b[1] - a[1])[0]

  if (rows.length === 0) {
    return <Empty>Nobody has bet a nickel yet.</Empty>
  }

  const nameOf = (m: BookMarket) =>
    m.subject_id ? playerName(doc, m.subject_id) : m.description

  return (
    <>
      <div className="px-4 py-3 border-b border-rule">
        <p className="text-[13px] leading-snug">
          <b className="bc-num text-base">{formatMoney(staked)}</b>
          <span className="text-mute">
            {' '}
            down across {live.length + pending.length} slips
          </span>
          {pending.length > 0 && (
            <span className="text-mute">
              {' '}· {formatMoney(pending.reduce((n, b) => n + b.stake_cents, 0))} of it
              still waiting on Venmo
            </span>
          )}
        </p>
        {biggest && heaviest && (
          <p className="text-[12px] text-mute leading-snug mt-1">
            Biggest single slip: {playerName(doc, biggest.player_id)} for{' '}
            {formatMoney(biggest.stake_cents)}. Most money out:{' '}
            {playerName(doc, heaviest[0])} at {formatMoney(heaviest[1])}.
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 px-4 py-2 border-b border-rule">
        <span className="bc-label text-mute shrink-0">Sort</span>
        <select
          value={view}
          onChange={(e) => {
            setView(e.target.value as View)
            setOpen(null)
          }}
          className="bc-tap border border-ink px-2 py-1.5 bg-paper text-sm flex-1 min-w-0"
        >
          <option value="money">Money on it</option>
          <option value="payout">What it pays if it lands</option>
          <option value="man">Who has the most out there</option>
          <option value="new">Newest bet first</option>
        </select>
      </div>

      {view === 'man' ? (
        <ByMan doc={doc} bets={bets} markets={markets} me={me} />
      ) : view === 'new' ? (
        <Newest doc={doc} bets={bets} markets={markets} me={me} />
      ) : (
        <>
      {rows.length > 0 && (
        <SectionRule>{view === 'payout' ? 'What it would cost' : 'Where the money is'}</SectionRule>
      )}
      <ul className="divide-y divide-rule border-t border-rule">
        {rows.map(({ market, exposure, slips, placed }) => {
          const isOpen = open === market.id
          return (
            <li key={market.id}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : market.id)}
                className="bc-tap w-full text-left px-4 py-2.5"
              >
                <span className="bc-name text-sm leading-snug block">{nameOf(market)}</span>
                <span className="flex items-baseline justify-between gap-3 mt-0.5">
                  <span className="text-[12px] text-mute min-w-0 truncate">
                    {slips.length} {slips.length === 1 ? 'man' : 'men'} on it
                    {exposure.handleCents < placed &&
                      ` · ${formatMoney(placed - exposure.handleCents)} unconfirmed`}
                  </span>
                  <span className="bc-num text-base shrink-0">{formatMoney(placed)}</span>
                </span>
              </button>

              {isOpen && (
                <ul className="px-4 pb-3">
                  {slips.map((b, i) => (
                    <li
                      key={i}
                      className={`flex items-baseline justify-between gap-3 text-[13px] py-0.5 ${
                        b.player_id === me ? 'bc-name' : ''
                      }`}
                    >
                      <span className="min-w-0 truncate">
                        {playerName(doc, b.player_id)}
                        <span className="text-mute">
                          {' '}
                          {formatMoney(b.stake_cents)} at{' '}
                          {formatAmerican(b.odds_at_placement)}
                          {b.status === 'pending' && ' · awaiting Venmo'}
                        </span>
                      </span>
                      <span className="text-mute shrink-0 whitespace-nowrap">
                        to win{' '}
                        {formatMoney(payoutCents(b.stake_cents, b.odds_at_placement))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>

        </>
      )}

      <p className="px-4 py-3 text-[12px] text-mute leading-snug">
        Ranked by every dollar called, confirmed or not — a man who called his bet
        across the table is on it either way. The bookie&rsquo;s exposure figures still
        count only money that has landed. A market nobody has touched is not here.
      </p>
    </>
  )
}
